import React, { useState, useEffect } from 'react';
import { Sparkles, Check, Loader2 } from 'lucide-react';

// We only inject the specific pulsing animation for the AI icon here.
// All other layout and coloring is driven by your main.css file.
const aiLoaderAnimations = `
  @keyframes ai-pulse {
    0% { transform: scale(0.95); opacity: 0.8; filter: drop-shadow(0 0 2px var(--color-blue-400)); }
    50% { transform: scale(1.05); opacity: 1; filter: drop-shadow(0 0 10px var(--color-blue-400)); }
    100% { transform: scale(0.95); opacity: 0.8; filter: drop-shadow(0 0 2px var(--color-blue-400)); }
  }
  .ai-pulse-icon { 
    animation: ai-pulse 2s infinite ease-in-out; 
    color: var(--brand-primary);    
  }
`;

export const AiExecutionLoader = ({
  isOpen,
  steps = [
    "Analyzing input data...",
    "Processing topics and sub topics ...",
    "Generating output...",
    "Reviewing output...",
    "Finalizing results..."
  ],
  title = "AI is working...",
  totalDuration = 5000, // Total expected API time in milliseconds
  onDismiss
}) => {
  const [currentStep, setCurrentStep] = useState(0);

  // Inject the pulse animation safely
  useEffect(() => {
    const styleId = 'ai-loader-animations';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = aiLoaderAnimations;
      document.head.appendChild(style);
    }
  }, []);

  // Handle the timed step progression
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(0); // Reset state when closed
      return;
    }

    // Mathematically divide the total expected time across all steps
    const durationPerStep = totalDuration / Math.max(steps.length, 1);

    const timer = setInterval(() => {
      setCurrentStep(prev => {
        if (prev >= steps.length - 1) {
            clearInterval(timer);
            return prev; // Hold at the final step until the API actually resolves and closes the modal
        }
        return prev + 1;
      });
    }, durationPerStep);
    
    return () => clearInterval(timer);
  }, [isOpen, steps.length, totalDuration]);

  if (!isOpen) return null;

  return (
    // Uses your global .custom-modal-overlay class
    <div className="custom-modal-overlay" style={{ zIndex: 1200, backgroundColor: 'rgba(0,0,0,0.6)' }}>
      
      {/* Uses your global .custom-modal-dialog class */}
      <div 
        className="custom-modal-dialog" 
        style={{ 
          maxWidth: '420px', 
          height: 'auto', 
          padding: '2.5rem 2rem', 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center' 
        }}
      >
        <div className="ai-pulse-icon" style={{ margin: '0 auto 1.5rem auto' }}>
           <Sparkles size={48} strokeWidth={1.5} />
        </div>
        
        <h4 className="fw-bold" style={{ marginBottom: '2rem', color: 'var(--brand-text)', textAlign: 'center' }}>
            {title}
        </h4>

        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {steps.map((step, idx) => {
             const isCompleted = idx < currentStep;
             const isActive = idx === currentStep;
             const isPending = idx > currentStep;

             return (
               <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '16px', opacity: isPending ? 0.3 : 1, transition: 'opacity 0.4s ease' }}>
                  <div style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                     {isCompleted && <Check size={20} strokeWidth={3} color="var(--color-success-text)" />}
                     
                     {/* Lucide's Loader2 comes with a built-in spin animation when class 'lucide-spin' is added or wrapping it in an animation block, but passing a class is cleaner */}
                     {isActive && <Loader2 size={18} color="var(--brand-primary)" className="lucide-spin" style={{ animation: 'ai-spinner-spin 1s linear infinite' }} />}
                     
                     {isPending && <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'var(--ui-border)' }}></div>}
                  </div>
                  <span style={{ fontSize: 'var(--font-size-base)', fontWeight: isActive ? 600 : 400, color: isActive ? 'var(--brand-primary)' : 'var(--ui-text-dark)', transition: 'all 0.3s' }}>
                    {step}
                  </span>
               </div>
             );
          })}
        </div>

        {onDismiss && (
            <div style={{ marginTop: '3.5rem', textAlign: 'center', width: '100%' }}>
               {/* Uses your global .btn and .btn-outline-primary classes */}
               <button 
                  className="btn btn-outline-primary"
                  onClick={onDismiss} 
                  style={{ borderRadius: '50rem', padding: '8px 24px' }}
                >
                  Run in background
               </button>
               <p className="text-sm text-muted" style={{ marginTop: '12px', marginBottom: 0 }}>
                  You can safely close this modal. The generation will continue.
               </p>
            </div>
        )}
      </div>
      
      {/* Fallback spinner animation if lucide-spin isn't globally defined */}
      <style>{`@keyframes ai-spinner-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AiExecutionLoader;

